import { ReactNode, useEffect, useRef } from 'react';
import { BACKDROP_COLOR_VARIANTS } from './constants';
import { BackdropBackgroundType, IAnimationOptions } from './types';
import * as Styles from './styles';
import { useBoolean } from '~/hooks/useBoolean';
import useScreenLock from '~/hooks/modal';
import { detectKeyPress } from '~/utils/utils';

interface IBackdropPorps {
  children: ReactNode;
  backdropBackground?: BackdropBackgroundType;
  onClick?: () => void;
  options?: IAnimationOptions;
  zIndex?: number;
}

const GLOBAL_MODAL_COUNT = {
  counter: 0,
};

const Backdrop = ({
  children,
  onClick,
  backdropBackground = 'light',
  options,
  zIndex = 1000,
}: IBackdropPorps) => {
  const [isMounted, mountedActions] = useBoolean();

  useScreenLock(true);
  const currentCount = useRef(GLOBAL_MODAL_COUNT.counter + 1);

  function handleKeyEvent(event: KeyboardEvent & { isCloseHandled?: boolean }) {
    if (detectKeyPress(event).escape && !event.isCloseHandled) {
      if (currentCount.current === GLOBAL_MODAL_COUNT.counter) {
        event.isCloseHandled = true;
        onClick?.();
      }
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyEvent);
    GLOBAL_MODAL_COUNT.counter += 1;
    mountedActions.on();

    return () => {
      document.removeEventListener('keydown', handleKeyEvent);
      GLOBAL_MODAL_COUNT.counter -= 1;
    };
  }, []);

  return (
    <Styles.BackdropWrapper
      onClick={onClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        delay: (isMounted ? options?.backdropExitDelay : options?.backdropEnterDelay) ?? 0,
        delayChildren: (isMounted ? options?.childrenExitDelay : options?.childrenEnterDelay) ?? 0,
      }}
      backdropBackground={BACKDROP_COLOR_VARIANTS[backdropBackground]}
      className="center"
      zIndex={zIndex}
      data-modal-layer
    >
      {children}
    </Styles.BackdropWrapper>
  );
};

export default Backdrop;
